import { MyList } from '@travel/icons/ui';
import { MOBILE_SCREEN, useDeviceType } from '@travel/traveler-core/hooks';
import { IconTextTag } from '@travel/ui';

import type { PlanListWithArrayIndex, Provider } from 'Plan-Types';
import type { ProviderWithArrayIndex } from 'Provider-Types';

import useRatBindClickComponent from '@travel/traveler-core/hooks/useRatBindClickComponent';
import { LinkWithRef } from 'favoriteList/core/universalRouter/Link/LinkWithRef';
import { useRef } from 'react';
import {
  getRatId,
  CLICK_FAVBUTTON,
  APPEAR_FAVBUTTON,
} from 'favoriteList/components/DataLayer/helper';

import styles from './favoriteButton.module.scss';

export enum FavoriteButtonType {
  PROVIDER = 'provider',
  PLAN = 'plan',
}

type Props<T extends FavoriteButtonType> = T extends 'provider'
  ? {
      type: FavoriteButtonType.PROVIDER;
      isDomestic: boolean;
      provider?: ProviderWithArrayIndex;
      handleProviderFavoriteButtonClick?: (provider: ProviderWithArrayIndex) => void;
    }
  : {
      type: FavoriteButtonType.PLAN;
      isDomestic: boolean;
      provider?: Provider;
      plan?: PlanListWithArrayIndex;
      handlePlanFavoriteButtonClick?: (plan: PlanListWithArrayIndex) => void;
    };

const FavoriteButton = (props: Props<FavoriteButtonType>) => {
  const { type, isDomestic } = props;
  const deviceType = useDeviceType();
  const isSP = deviceType === MOBILE_SCREEN;
  const ratLink = useRef<HTMLAnchorElement>(null);
  const ratAppear = useRef(null);
  const isPlanList = type === 'plan';

  useRatBindClickComponent(ratLink);
  useRatBindClickComponent(ratAppear);

  const onClickHandler = () => {
    ratLink.current?.click();
    if (type === FavoriteButtonType.PROVIDER) {
      const { provider, handleProviderFavoriteButtonClick } = props;
      if (provider && handleProviderFavoriteButtonClick) {
        handleProviderFavoriteButtonClick(provider);
      }
    } else if (type === FavoriteButtonType.PLAN) {
      const { plan, handlePlanFavoriteButtonClick } = props;
      if (plan && handlePlanFavoriteButtonClick) {
        handlePlanFavoriteButtonClick(plan);
      }
    }
  };

  const testId =
    type === FavoriteButtonType.PROVIDER
      ? props.provider?.hotelNo
      : props.type === FavoriteButtonType.PLAN
        ? props.plan?.id
        : undefined;

  const provider = props.provider;
  const plan = type === FavoriteButtonType.PLAN ? props.plan : undefined;

  return (
    <div>
      <IconTextTag
        className={styles.customIconClass}
        color="default"
        icon={
          <button
            data-testid={`favorite-button-${isSP ? 'sp' : 'pc'}-${testId}`}
            className={styles.favoriteButton}
            onClick={onClickHandler}
            data-ratevent="appear"
            data-ratParam="all"
            data-ratid={getRatId(APPEAR_FAVBUTTON, isPlanList, isSP)}
            {...(isDomestic && {
              ['data-rat-shopId']: provider?.hotelNo,
              ['data-rat-itemid']: plan?.id,
            })}
            ref={ratAppear}
          >
            <MyList color="appleRed" />
          </button>
        }
      />
      <LinkWithRef
        data-ratevent="click"
        data-ratParam="all"
        data-ratid={getRatId(CLICK_FAVBUTTON, isPlanList, isSP)}
        {...(isDomestic && {
          ['data-rat-shopId']: provider?.hotelNo,
          ['data-rat-itemid']: plan?.id,
        })}
        ref={ratLink}
        to={''}
      >
        {null}
      </LinkWithRef>
    </div>
  );
};
export default FavoriteButton;
